import { useAutoAnimate } from '@formkit/auto-animate/react';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import FormInput from 'components/common/form-input';
import ProjectSetting from 'components/common/project-setting';
import SectionLoadingOverlay from 'components/common/section-loading-overlay';
import QuantityInput from 'components/inputs/quantity';
import ComfortSettingsFields from 'components/structure-page/comfort-settings-fields';
import EstimatedLoadCard from 'components/structure-page/estimated-load-card';
import { setProjectSettingValue } from 'features/project';
import {
  setStructureFoundation,
  setStructureHasAttic,
  setStructureLevelCount
} from 'features/structure';
import { setSquareFootageWarningShown } from 'features/ui';
import useIsSimpleSizingPanel from 'hooks/use-is-simple-sizing-panel';
import useSizingEstimate from 'hooks/use-sizing-estimate';
import { getProjectSettingValue } from 'utils/project';
import { toFiniteNumber } from 'utils/to-finite-number';
import {
  FoundationTypeType,
  getFoundationType,
  getHasAttic,
  getLevelCount,
  getValidLevelCodes
} from 'utils/structure-basics';
import { warningModal } from 'utils/ui';
import {
  applicableWindowsAnswer,
  windowAnswerOptions
} from 'utils/window-answer-options';

const SQUARE_FOOTAGE_WARNING_MESSAGE =
  "We've automatically pulled in the square footage from your structure. Please make sure this reflects your structure's entire square footage.";

export default function StructureDetailsStep() {
  const dispatch = useAppDispatch();
  const { structure, structureSubtypeId } = useAppSelector(
    (state) => state.structure
  );
  const { items, validItems } = useAppSelector((state) => state.data);
  const { levelsLoading } = useAppSelector((state) => state.ai);
  const squareFootageWarningShown = useAppSelector(
    (state) => state.ui.squareFootageWarningShown
  );
  const projectSettings = useAppSelector(
    (state) => state.project.projectSettings
  );
  const totalBTUs = useAppSelector((state) => state.project.totalBTUs);
  const isSimpleSizingPanel = useIsSimpleSizingPanel();
  const { status: estimateStatus } = useSizingEstimate(isSimpleSizingPanel);
  const [animate] = useAutoAnimate({ duration: 100 });

  const settingValue = (code: string): unknown =>
    projectSettings.find((setting) => setting.code === code)?.value;

  const insulationValue = settingValue('insulation_level');
  const insulation =
    typeof insulationValue === 'string' ? insulationValue : null;
  const desiredHeating = toFiniteNumber(settingValue('desired_heating')) ?? 70;
  const desiredCooling = toFiniteNumber(settingValue('desired_cooling')) ?? 75;
  const yearBuilt = toFiniteNumber(settingValue('year_built'));
  const hasWindowsSetting = projectSettings.some(
    (setting) => setting.code === 'windows'
  );
  // Asked only of homes that could have replaced their windows, with the
  // decades a home of this age could have done it in. The stored answer is
  // kept applicable where year_built is written (the project slice), so
  // here it only needs reading.
  const windowOptions = windowAnswerOptions(yearBuilt);
  const windowsAnswer = applicableWindowsAnswer(
    settingValue('windows'),
    yearBuilt
  );
  const showWindowsRow =
    isSimpleSizingPanel && hasWindowsSetting && windowOptions !== null;

  const validLevelCodes = getValidLevelCodes(
    items,
    validItems,
    structureSubtypeId
  );
  const canHaveLevels = validLevelCodes.includes('level');
  const canHaveBasement = validLevelCodes.includes('basement');
  const canHaveCrawlspace = validLevelCodes.includes('crawlspace');
  const canHaveAttic = validLevelCodes.includes('attic');

  const levelCount = getLevelCount(structure);
  const foundation = getFoundationType(structure);
  const hasAttic = getHasAttic(structure);

  const foundationOptions: { value: FoundationTypeType; label: string }[] = [
    ...(canHaveBasement
      ? [{ value: 'basement' as const, label: 'Basement' }]
      : []),
    ...(canHaveCrawlspace
      ? [{ value: 'crawlspace' as const, label: 'Crawlspace' }]
      : []),
    { value: 'none', label: 'Solid Ground' }
  ];

  const handleSquareFootageFocus = () => {
    const isPrimaryStructure =
      getProjectSettingValue('primary_or_detached_structure') ===
      'Primary Structure';

    if (isPrimaryStructure && !squareFootageWarningShown) {
      warningModal(SQUARE_FOOTAGE_WARNING_MESSAGE);
      dispatch(setSquareFootageWarningShown(true));
    }
  };

  return (
    <div className='flex w-full flex-col gap-5 p-5' ref={animate}>
      <div className='w-full overflow-auto'>
        <div className='mx-auto w-fit'>
          <div className='mb-2 border-b border-gray-300 pb-2 text-xl font-bold text-gray-700'>
            Basic Info
          </div>
          <div className='relative'>
            <SectionLoadingOverlay loading={levelsLoading} />
            <table className='w-full'>
              <tbody id='structure-details-basics'>
                <tr>
                  <td className='p-2 text-right text-lg font-semibold'>
                    Total square footage:
                  </td>
                  <td className='p-2'>
                    <div onFocus={handleSquareFootageFocus}>
                      <ProjectSetting
                        code='square_footage'
                        required
                        width={120}
                      />
                    </div>
                  </td>
                </tr>
                {canHaveLevels && (
                  <tr>
                    <td className='p-2 text-right text-lg font-semibold'>
                      Number of levels:
                    </td>
                    <td className='py-2'>
                      <QuantityInput
                        value={levelCount}
                        debounce={600}
                        invalid={levelCount < 1}
                        onChange={(value) => {
                          if (
                            typeof value === 'number' &&
                            !Number.isNaN(value)
                          ) {
                            dispatch(setStructureLevelCount(value));
                          }
                        }}
                      />
                    </td>
                  </tr>
                )}
                {(canHaveBasement || canHaveCrawlspace) && (
                  <tr>
                    <td className='p-2 text-right text-lg font-semibold'>
                      Foundation:
                    </td>
                    <td className='py-2'>
                      <FormInput
                        type='select'
                        value={foundation}
                        options={foundationOptions}
                        preventDeselection
                        onChange={(value) =>
                          dispatch(
                            setStructureFoundation(value as FoundationTypeType)
                          )
                        }
                      />
                    </td>
                  </tr>
                )}
                {canHaveAttic && (
                  <tr>
                    <td className='p-2 text-right text-lg font-semibold'>
                      Is there an attic?
                    </td>
                    <td className='py-2'>
                      <FormInput
                        type='select'
                        value={hasAttic ? 'Yes' : 'No'}
                        options={['Yes', 'No']}
                        preventDeselection
                        onChange={(value) =>
                          dispatch(setStructureHasAttic(value === 'Yes'))
                        }
                      />
                    </td>
                  </tr>
                )}
                {showWindowsRow && (
                  <tr>
                    <td className='p-2 text-right text-lg font-semibold'>
                      Windows:
                    </td>
                    <td className='py-2'>
                      <FormInput
                        type='select'
                        value={windowsAnswer ?? ''}
                        options={windowOptions}
                        placeholder='Not sure'
                        enableChips={false}
                        onChange={(value) =>
                          dispatch(
                            setProjectSettingValue(
                              'windows',
                              typeof value === 'string' && value !== ''
                                ? value
                                : undefined
                            )
                          )
                        }
                      />
                    </td>
                  </tr>
                )}
                {isSimpleSizingPanel && (
                  <ComfortSettingsFields
                    insulation={insulation}
                    desiredHeating={desiredHeating}
                    desiredCooling={desiredCooling}
                    onInsulationChange={(value) =>
                      dispatch(
                        setProjectSettingValue('insulation_level', value)
                      )
                    }
                    onDesiredHeatingChange={(value) =>
                      dispatch(setProjectSettingValue('desired_heating', value))
                    }
                    onDesiredCoolingChange={(value) =>
                      dispatch(setProjectSettingValue('desired_cooling', value))
                    }
                  />
                )}
              </tbody>
            </table>
          </div>
          {isSimpleSizingPanel && (
            <div className='mt-6 flex flex-col items-start'>
              <EstimatedLoadCard
                status={estimateStatus}
                totals={totalBTUs}
                pendingMessage="Crunching the numbers from your home's basics and local climate…"
                emptyMessage="We'll estimate the load once the basics above are filled in."
                footnote="A Manual J estimate for a typical home like yours: your home's size, levels, foundation, age or insulation, windows, local climate, and thermostat settings"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
